import jsonata from 'jsonata';
import type {
  Condition,
  ConditionGroup,
  FormData,
} from '../logic-builder-types';
import { FieldTypes } from '../../../constants';

function stringToNumberConversion(value: string) {
  if (value === '') {
    return undefined;
  }
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
}

/**
 * Formats form data by converting number field values to actual numbers
 * while preserving string values for specific string-based operators
 */
function formatFormData(condition: Condition, formData: FormData): FormData {
  const { fieldType, fieldId, textType } = condition;

  if (fieldType !== FieldTypes.TYPE_NUMBER_FIELD && textType !== 'number') {
    return formData;
  }

  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2538
  const fieldValue = formData[fieldId];
  if (typeof fieldValue !== 'string') {
    return formData;
  }

  const numberValue = stringToNumberConversion(fieldValue);
  if (numberValue === null) {
    throw new Error(
      'Error evaluating condition: Number field value is invalid',
    );
  }

  // Return new form data with converted number
  return {
    ...formData,
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2464
    [fieldId]: numberValue,
  };
}

async function evaluateCondition(
  condition: Condition,
  formData: FormData,
): Promise<boolean> {
  const { expression } = condition;

  // Check if expression is defined
  if (expression === undefined || expression === null) {
    throw new Error('Error evaluating condition: Expression field is missing');
  }

  let result;

  /**
   * So things are a little rough here.  First is JSONata, which has a well formed interface with exceptions and
   * all that goodness.  Second we have annon functions that do checks that require more nuance than JSONata can
   * provide. Though both are ecapsulated in a try/catch block, there is no contract that the annon functions will
   * use exceptions to enforce their contracts.  At some point we will want to migrate the JSONata to functions, OR
   * we will want to use a wrapper object that homoginizes the interface of the two.
   */
  try {
    if (typeof expression !== 'string') {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2349
      result = expression(formData); // also not a promise... another delta to consider
    } else {
      const jsonataExpression = jsonata(expression);
      const formattedData = formatFormData(condition, formData);
      result = await jsonataExpression.evaluate(formattedData);
    }
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  } catch (error: any) {
    throw new Error(`Error evaluating JSONata expression: ${error.message}`);
  }

  if (typeof result !== 'boolean') {
    if (result === undefined) {
      return false; // handle undefined results
    }
    throw new Error(
      `Unexpected result type: ${typeof result}. Expected a boolean value.`,
    );
  }

  return result;
}

async function evaluateConditionGroup(
  conditionGroup: ConditionGroup,
  formData: FormData,
): Promise<boolean> {
  const { matchConditions, conditions } = conditionGroup;

  if (matchConditions === 'all') {
    const results = await Promise.all(
      conditions.map(condition => evaluateCondition(condition, formData)),
    );
    return results.every(Boolean);
  }
  if (matchConditions === 'any') {
    const results = await Promise.all(
      conditions.map(condition => evaluateCondition(condition, formData)),
    );
    return results.some(Boolean);
  }

  throw new Error(`Unknown match conditions: ${matchConditions}`);
}

export { evaluateCondition, evaluateConditionGroup };
